import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ModelListItemFragment } from "@/apollo/types";

import { usePublishedModels } from "./PublishedModelsProvider";
import { useRawViews } from "./RawViewsProvider";

type ReferenceMapValueType =
  | ModelListItemFragment["dwSync"]
  | ModelListItemFragment["dwTable"]
  | ReturnType<typeof useRawViews>["rawViews"][number];

type ReferenceMapType = Map<string, ReferenceMapValueType>;

const ReferenceMapContext = createContext<ReferenceMapType | null>(null);

type ReferenceMapGetter = () => ReferenceMapType;
const ReferenceMapGetterContext = createContext<ReferenceMapGetter | null>(
  null,
);

type ReferenceMapReadiness = { loading: boolean; loaded: Promise<void> };
const ReferenceMapReadinessContext =
  createContext<ReferenceMapReadiness | null>(null);

export function ModelDependenciesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { publishedModels: models, loadingModels } = usePublishedModels();
  const { rawViews, loading: loadingRawViews } = useRawViews();

  const map = useMemo(() => {
    const map = new Map<string, ReferenceMapValueType>();

    for (const rawView of rawViews) {
      map.set(rawView.weldTag, rawView);
    }

    for (const model of models) {
      const dwItem = model.dwSync ?? model.dwTable;
      if (!dwItem) {
        continue;
      }
      if (dwItem.weldTag) {
        map.set(dwItem.weldTag, dwItem);
      }
    }

    return map;
  }, [models, rawViews]);

  // Create a ref to hold the current publishedModels value.
  const modelDependenciesRef = useRef<ReferenceMapType>(map);

  // Update the ref whenever the publishedModels part of our state changes.
  useEffect(() => {
    modelDependenciesRef.current = map;
  }, [map]);

  // Stable getter function that always returns the latest published models.
  const getMap = useCallback(() => {
    return modelDependenciesRef.current;
  }, []);

  const loading = loadingModels || loadingRawViews;
  const [loaded] = useState(createDeferred);
  useEffect(() => {
    if (!loading) loaded.resolve();
  }, [loading, loaded]);
  const readiness = useMemo(
    () => ({ loading, loaded: loaded.promise }),
    [loading, loaded],
  );

  return (
    <ReferenceMapContext.Provider value={map}>
      <ReferenceMapGetterContext.Provider value={getMap}>
        <ReferenceMapReadinessContext.Provider value={readiness}>
          {children}
        </ReferenceMapReadinessContext.Provider>
      </ReferenceMapGetterContext.Provider>
    </ReferenceMapContext.Provider>
  );
}

export const useModelDependenciesMap = () => {
  const context = useContext(ReferenceMapContext);
  if (!context) {
    throw new Error(
      "useModelDependenciesMap must be used within a ReferenceMapContext",
    );
  }
  return context;
};

export const useModelDepedenciesMapGetter = () => {
  const getMap = useContext(ReferenceMapGetterContext);
  if (!getMap) {
    throw new Error(
      "useModelDepedenciesMapGetter must be used within a ReferenceMapGetterContext",
    );
  }
  return getMap;
};

function useReferenceMapReadiness() {
  const readiness = useContext(ReferenceMapReadinessContext);
  if (!readiness) {
    throw new Error(
      "useReferenceMapReadiness must be used within a ModelDependenciesProvider",
    );
  }
  return readiness;
}

/**
 * True until both published models and raw views have loaded. Until then a
 * reference missing from the map may only be missing because its list has not
 * arrived, so nothing should report it as invalid.
 */
export const useModelDependenciesLoading = () =>
  useReferenceMapReadiness().loading;

/**
 * Settles once the first published models and raw views requests have,
 * whether or not they succeeded.
 */
export const useModelDependenciesLoaded = () =>
  useReferenceMapReadiness().loaded;

function createDeferred() {
  let resolve!: () => void;
  const promise = new Promise<void>((r) => (resolve = r));
  return { promise, resolve };
}
